import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";

import { AttributeCard } from "@/components/Card/AttributeCard";
import { Card } from "@/components/Card/card";
import { SimpleDatePicker } from "@/components/DatePicker/SimpleDatePicker";
import StepFooter from "@/components/Footer/StepFooter";
import { Spacer, VStack } from "@/components/Layout";
import { ControlledSelect } from "@/components/Select/ControlledSelect";
import { TextVariant } from "@/components/Typography";
import { Text } from "@/components/Typography/Text";
import useZodValidation from "@/hooks/useZodValidation";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { FormField } from "@/routes/_staff/person/-components/form/FormField";
import { formatISODate } from "@/utils/date/formatISODate";

import { ManualFormHash } from "../-constants/constant";
import { useCreateNetwork } from "../-form/createNetworkApi";
import { useAddNetworkFormStore } from "../-store/add-network-form-store";
import { useNetworkFormHelpers } from "../../-hooks/useNetworkFormHelpers";

export function Step2Form() {
  useZodValidation();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.networks.add.step2",
  });
  const navigate = useNavigate();
  const store = useAddNetworkFormStore();
  const { submitNetwork, isSubmitting } = useCreateNetwork();

  const { accessLevelOptions, accessStatusOptions, subjectTypeOptions } =
    useNetworkFormHelpers();

  const handleSubmit = useMemo(
    () => async () => {
      try {
        await submitNetwork();
      } catch (error) {
        console.error("Failed to create network:", error);
      }
    },
    [submitNetwork],
  );

  const handleBack = () => {
    navigate({ hash: ManualFormHash.networkInformation });
  };

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <VStack className="gap-6">
        <Card className="p-4">
          <Text variant="h3">{`2. ${t("title")}`}</Text>
        </Card>

        <AttributeCard>
          <FormField
            className="mt-4"
            fieldTitle={t("accessStatus.label")}
            titleVariant={TextVariant.CardHeader}
            titleClassName="text-neutral-on-fill"
            fieldInput={
              <ControlledSelect
                id="accessStatus"
                value={store.accessStatus || ""}
                onChange={(option) =>
                  store.update(
                    "accessStatus",
                    option?.value as typeof store.accessStatus,
                  )
                }
                fetchOptions={() => Promise.resolve(accessStatusOptions)}
                hideSearch={true}
                error={!!store.errors.accessStatus}
                allowEmpty={true}
              />
            }
            optional={true}
          />
        </AttributeCard>

        <AttributeCard>
          <FormField
            className="mt-4"
            fieldTitle={t("accessLevel.label")}
            titleVariant={TextVariant.CardHeader}
            titleClassName="text-neutral-on-fill"
            fieldInput={
              <ControlledSelect
                id="accessLevel"
                value={store.accessLevel || ""}
                onChange={(option) =>
                  store.update(
                    "accessLevel",
                    option?.value as typeof store.accessLevel,
                  )
                }
                fetchOptions={() => Promise.resolve(accessLevelOptions)}
                hideSearch={true}
                error={!!store.errors.accessLevel}
                allowEmpty={true}
              />
            }
            optional={true}
          />
        </AttributeCard>

        <AttributeCard>
          <FormField
            className="mt-4"
            fieldTitle={t("accessPeriodFrom.label")}
            titleVariant={TextVariant.CardHeader}
            titleClassName="text-neutral-on-fill"
            fieldInput={
              <SimpleDatePicker
                id="accessPeriodStartDate"
                value={
                  store.accessPeriodStartDate
                    ? new Date(store.accessPeriodStartDate)
                    : undefined
                }
                onChange={(date) =>
                  store.update(
                    "accessPeriodStartDate",
                    date ? formatISODate(date) : undefined,
                  )
                }
                width={180}
                allowEmpty={true}
              />
            }
            optional={true}
          />
        </AttributeCard>

        <AttributeCard>
          <FormField
            className="mt-4"
            fieldTitle={t("accessPeriodTo.label")}
            titleVariant={TextVariant.CardHeader}
            titleClassName="text-neutral-on-fill"
            fieldInput={
              <SimpleDatePicker
                id="accessPeriodEndDate"
                value={
                  store.accessPeriodEndDate
                    ? new Date(store.accessPeriodEndDate)
                    : undefined
                }
                onChange={(date) =>
                  store.update(
                    "accessPeriodEndDate",
                    date ? formatISODate(date) : undefined,
                  )
                }
                width={180}
                allowEmpty={true}
              />
            }
            optional={true}
          />
        </AttributeCard>

        <AttributeCard>
          <FormField
            className="mt-4"
            fieldTitle={t("subjectType.label")}
            titleVariant={TextVariant.CardHeader}
            titleClassName="text-neutral-on-fill"
            fieldInput={
              <ControlledSelect
                id="subjectType"
                value={store.subjectType || ""}
                onChange={(option) =>
                  store.update(
                    "subjectType",
                    option?.value as typeof store.subjectType,
                  )
                }
                fetchOptions={() => Promise.resolve(subjectTypeOptions)}
                hideSearch={true}
                error={!!store.errors.subjectType}
                allowEmpty={true}
              />
            }
            optional={true}
          />
        </AttributeCard>
      </VStack>
      <Spacer variant="xxl" />
      <StepFooter
        leftButton={{
          onClick: handleBack,
        }}
        rightButton={{
          onClick: handleSubmit,
          label: isSubmitting ? t("submitting") : t("submit"),
          disabled: isSubmitting,
        }}
      />
    </form>
  );
}
